import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import {
  GUIDE_ROUTES,
  MANAGEMENT_GUIDE_CONTENT_BY_LANGUAGE,
  MANAGEMENT_GUIDE_LANGUAGE_STORAGE_KEY,
} from './content';
import './managementGuide.css';

function createLessonModel(copy) {
  const startLesson = {
    key: copy.navigation.start.key,
    title: copy.navigation.start.title,
    group: copy.ui.startHere,
  };
  const foundationLessons = copy.foundation.map((lesson, index) => ({
    ...lesson,
    key: lesson.lessonKey,
    group: copy.ui.foundation,
    position: index + 1,
  }));
  const domainGroups = copy.domainGroups.map((group) => ({
    ...group,
    lessons: group.lessons.map((lesson) => ({
      ...lesson,
      key: lesson.lessonKey,
      domainKey: group.key,
      group: group.name,
      position: Number.parseInt(lesson.number, 10),
    })),
  }));
  const domainLessons = domainGroups.flatMap((group) => group.lessons);
  const implementedLessons = [startLesson, ...foundationLessons, ...domainLessons];

  return {
    startLesson,
    foundationLessons,
    domainGroups,
    domainLessons,
    implementedLessons,
    implementedLessonsByKey: Object.fromEntries(
      implementedLessons.map((lesson) => [lesson.key, lesson]),
    ),
  };
}

function formatMessage(template, values) {
  return Object.entries(values).reduce(
    (message, [key, value]) => message.replace(`{${key}}`, value),
    template,
  );
}

function getInitialLanguage() {
  try {
    const savedLanguage = window.localStorage.getItem(MANAGEMENT_GUIDE_LANGUAGE_STORAGE_KEY);
    return savedLanguage === 'ro' ? 'ro' : 'en';
  } catch (error) {
    return 'en';
  }
}

function GuideLanguageSelector({ language, onChange, label }) {
  return (
    <Box component="div" role="group" aria-label={label} className="management-guide-language-selector">
      <Button
        type="button"
        size="small"
        aria-label="English"
        aria-pressed={language === 'en'}
        className={language === 'en' ? 'is-active' : undefined}
        onClick={() => onChange('en')}
      >
        EN
      </Button>
      <Button
        type="button"
        size="small"
        aria-label="Română"
        aria-pressed={language === 'ro'}
        className={language === 'ro' ? 'is-active' : undefined}
        onClick={() => onChange('ro')}
      >
        RO
      </Button>
    </Box>
  );
}

function ManualLink({ label, route }) {
  return (
    <Button
      component={RouterLink}
      to={route}
      target="_blank"
      rel="noopener noreferrer"
      variant="text"
      size="small"
      className="management-guide-manual-link management-guide-new-tab-link"
    >
      {label}
    </Button>
  );
}

function getLessonGroupMeta(copy, lessons) {
  const first = lessons[0]?.position;
  const last = lessons[lessons.length - 1]?.position;

  if (first === last) {
    return formatMessage(copy.ui.lessonNumber, { number: first });
  }

  return formatMessage(copy.ui.lessonRange, { first, last });
}

function NavigationGroup({
  copy,
  group,
  activeKey,
  activeItemRef,
  expanded,
  onToggle,
  onSelect,
  testId,
}) {
  const listId = `${testId}-${group.key}-lessons`;
  const containsActiveLesson = group.lessons.some((lesson) => lesson.key === activeKey);

  return (
    <Box className={containsActiveLesson
      ? 'management-guide-navigation-group has-active-lesson'
      : 'management-guide-navigation-group'}
    >
      <button
        type="button"
        className="management-guide-navigation-group-toggle"
        aria-expanded={expanded}
        aria-controls={listId}
        onClick={onToggle}
      >
        <span className="management-guide-navigation-group-title">{group.name}</span>
        <small>{getLessonGroupMeta(copy, group.lessons)}</small>
        <span aria-hidden="true" className="management-guide-navigation-group-chevron">›</span>
      </button>
      <List
        id={listId}
        disablePadding
        hidden={!expanded}
        style={{ display: expanded ? undefined : 'none' }}
        className="management-guide-navigation-list"
      >
        {group.lessons.map((lesson) => (
          <ListItemButton
            key={lesson.key}
            component="button"
            type="button"
            ref={activeKey === lesson.key ? activeItemRef : undefined}
            selected={activeKey === lesson.key}
            aria-current={activeKey === lesson.key ? 'page' : undefined}
            onClick={() => onSelect(lesson.key)}
          >
            <span className="management-guide-navigation-number">{lesson.position}</span>
            <ListItemText primary={lesson.title} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
}

function LessonNavigation({
  copy,
  startLesson,
  foundationLessons,
  domainGroups,
  activeKey,
  onSelect,
  testId,
  revealActive = false,
  scrollContainerRef,
}) {
  const activeItemRef = useRef(null);
  const foundationGroup = {
    key: 'foundation',
    name: copy.ui.foundation,
    lessons: foundationLessons,
  };
  const lessonGroups = [foundationGroup, ...domainGroups];
  const activeGroupKey = lessonGroups.find((group) => (
    group.lessons.some((lesson) => lesson.key === activeKey)
  ))?.key || foundationGroup.key;
  const [openGroupKey, setOpenGroupKey] = useState(activeGroupKey);

  useEffect(() => {
    setOpenGroupKey(activeGroupKey);
  }, [activeGroupKey]);

  useEffect(() => {
    if (revealActive) setOpenGroupKey(activeGroupKey);
  }, [activeGroupKey, revealActive]);

  useEffect(() => {
    const item = activeItemRef.current;
    const container = scrollContainerRef?.current;
    if (!revealActive || !item || !container) return;

    const itemRect = item.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const safeInset = 10;

    if (itemRect.top < containerRect.top + safeInset) {
      container.scrollTop -= containerRect.top + safeInset - itemRect.top;
    } else if (itemRect.bottom > containerRect.bottom - safeInset) {
      container.scrollTop += itemRect.bottom - containerRect.bottom + safeInset;
    }
  }, [activeKey, openGroupKey, revealActive, scrollContainerRef]);

  return (
    <Box component="nav" aria-label={copy.ui.guideNavigation} data-testid={testId}>
      <Typography className="management-guide-navigation-label">{copy.ui.startHere}</Typography>
      <List disablePadding className="management-guide-navigation-list">
        <ListItemButton
          component="button"
          type="button"
          ref={activeKey === startLesson.key ? activeItemRef : undefined}
          selected={activeKey === startLesson.key}
          aria-current={activeKey === startLesson.key ? 'page' : undefined}
          onClick={() => onSelect(startLesson.key)}
        >
          <ListItemText primary={startLesson.title} />
        </ListItemButton>
      </List>

      {lessonGroups.map((group) => (
        <NavigationGroup
          key={group.key}
          copy={copy}
          group={group}
          activeKey={activeKey}
          activeItemRef={activeItemRef}
          expanded={openGroupKey === group.key}
          onToggle={() => setOpenGroupKey((currentKey) => (
            currentKey === group.key ? null : group.key
          ))}
          onSelect={onSelect}
          testId={testId}
        />
      ))}

      <Box
        component="details"
        className="management-guide-navigation-explore"
        data-testid={`${testId}-explore-topics`}
      >
        <Box component="summary">
          <span>{copy.ui.futureDomains}</span>
          <small>
            {formatMessage(
              copy.futureDomains.length === 1
                ? copy.ui.topicComingLater
                : copy.ui.topicsComingLater,
              { count: copy.futureDomains.length },
            )}
          </small>
        </Box>
        <List disablePadding className="management-guide-navigation-list management-guide-navigation-future">
          {copy.futureDomains.map((domain) => (
            <ListItemButton
              key={domain.name}
              component="button"
              type="button"
              disabled
              aria-label={`${domain.name}, ${copy.ui.comingLater.toLocaleLowerCase()}`}
            >
              <ListItemText primary={domain.name} secondary={copy.ui.comingLater} />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Box>
  );
}

function MobileLessonSelector({
  copy,
  startLesson,
  foundationLessons,
  domainGroups,
  numberedLessonCount,
  activeLesson,
  activeKey,
  open,
  onOpen,
  onClose,
  onSelect,
}) {
  const navigationScrollRef = useRef(null);
  const currentLabel = activeLesson.position
    ? formatMessage(copy.ui.lessonProgress, {
      current: activeLesson.position,
      total: numberedLessonCount,
    })
    : copy.ui.startHere;

  return (
    <>
      <Box className="management-guide-mobile-selector" data-testid="mobile-lesson-selector">
        <Button
          type="button"
          variant="outlined"
          fullWidth
          aria-expanded={open}
          aria-controls="management-guide-mobile-navigation"
          aria-label={formatMessage(copy.ui.chooseLessonCurrent, { title: activeLesson.title })}
          onClick={onOpen}
        >
          <span>
            <small>{currentLabel}</small>
            <strong>{activeLesson.title}</strong>
          </span>
          <span aria-hidden="true" className="management-guide-selector-symbol">{copy.ui.lessons}</span>
        </Button>
      </Box>
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          id: 'management-guide-mobile-navigation',
          className: 'management-guide-mobile-drawer',
        }}
      >
        <Box className="management-guide-mobile-drawer-header">
          <Box>
            <Typography className="management-guide-navigation-label">{copy.hero.title}</Typography>
            <Typography component="h2" variant="h6">{copy.ui.chooseLesson}</Typography>
          </Box>
          <Button type="button" size="small" onClick={onClose}>{copy.ui.close}</Button>
        </Box>
        <Divider />
        <Box ref={navigationScrollRef} className="management-guide-mobile-drawer-content">
          <LessonNavigation
            copy={copy}
            startLesson={startLesson}
            foundationLessons={foundationLessons}
            domainGroups={domainGroups}
            activeKey={activeKey}
            onSelect={onSelect}
            testId="mobile-lesson-navigation"
            revealActive={open}
            scrollContainerRef={navigationScrollRef}
          />
        </Box>
      </Drawer>
    </>
  );
}

function ReasoningRail({ copy, activeStage, activeStages = [] }) {
  const highlightedStages = activeStages.length > 0
    ? activeStages
    : [activeStage].filter(Boolean);

  return (
    <Box component="section" aria-labelledby="reasoning-heading" className="management-guide-reasoning">
      <Box className="management-guide-reasoning-heading">
        <Typography id="reasoning-heading" component="h3">{copy.ui.reasoningMethod}</Typography>
        <Typography>{copy.reasoning.prompt}</Typography>
      </Box>
      <Box component="ol" data-testid="reasoning-stages" className="management-guide-reasoning-stages">
        {copy.reasoning.stages.map((stage) => {
          const isActive = highlightedStages.includes(stage.name);
          return (
            <Box
              component="li"
              key={stage.name}
              className={isActive ? 'management-guide-reasoning-stage is-active' : 'management-guide-reasoning-stage'}
              aria-current={isActive && highlightedStages.length === 1 ? 'step' : undefined}
              aria-label={`${stage.name}: ${stage.question}`}
            >
              <span>{stage.name}</span>
              {isActive && <small>{stage.question}</small>}
            </Box>
          );
        })}
      </Box>
      <Box className="management-guide-reasoning-notes">
        <Typography>{copy.reasoning.principle}</Typography>
        <Typography><strong>{copy.ui.afterVerify}:</strong> {copy.reasoning.outcome}</Typography>
      </Box>
    </Box>
  );
}

function EvidenceReminder({ copy }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant="text"
        className="management-guide-reminder-trigger"
        onClick={() => setOpen(true)}
      >
        <strong>{copy.ui.beforeYouDecide}:</strong>&nbsp; {copy.reminder.map((item) => item.name).join(' · ')}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        className="management-guide-dialog"
        aria-labelledby="management-guide-reminder-title"
      >
        <DialogTitle id="management-guide-reminder-title">{copy.ui.beforeYouDecide}</DialogTitle>
        <DialogContent dividers>
          <List disablePadding className="management-guide-reminder-list">
            {copy.reminder.map((item) => (
              <Box component="li" key={item.name}>
                <Typography component="h3">{item.name}</Typography>
                <Typography>{item.question}</Typography>
              </Box>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setOpen(false)}>{copy.ui.close}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function KalopsiaDialog({ copy, lessonKey, lessonTitle, evidence }) {
  const [open, setOpen] = useState(false);
  const titleId = `management-guide-kalopsia-title-${lessonKey}`;

  return (
    <>
      <Button
        type="button"
        variant="outlined"
        size="small"
        onClick={() => setOpen(true)}
      >
        {copy.ui.useKalopsia}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        className="management-guide-dialog"
        aria-labelledby={titleId}
      >
        <DialogTitle id={titleId}>{copy.ui.useKalopsia}</DialogTitle>
        <DialogContent dividers>
          <Typography className="management-guide-dialog-context">
            {formatMessage(copy.ui.lessonContext, { title: lessonTitle })}
          </Typography>
          <Box className="management-guide-dialog-section">
            <Typography component="h3">{copy.ui.recommendedStartingArea}</Typography>
            <Typography className="management-guide-dialog-value">{evidence.startArea}</Typography>
            <Typography>{evidence.instruction}</Typography>
          </Box>
          <Box className="management-guide-dialog-section">
            <Typography component="h3">{copy.ui.continueInvestigating}</Typography>
            <Box component="ul" className="management-guide-dialog-list">
              {evidence.secondaryAreas.map((area) => <li key={area}>{area}</li>)}
            </Box>
          </Box>
          <Button
            component={RouterLink}
            to={GUIDE_ROUTES.dashboard}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            className="management-guide-open-button management-guide-new-tab-link"
          >
            {copy.ui.openDashboard}
          </Button>
          <Divider className="management-guide-dialog-divider" />
          <Typography component="h3" className="management-guide-dialog-manual-title">
            {copy.ui.relatedManuals}
          </Typography>
          <Box className="management-guide-dialog-manuals">
            {evidence.manuals.map((manual) => <ManualLink key={manual.route} {...manual} />)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setOpen(false)}>{copy.ui.close}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function PreviousNext({ copy, previous, next, returnLesson, onSelect }) {
  const nextAction = next || returnLesson;
  const isReturnAction = !next && Boolean(returnLesson);

  return (
    <Box component="nav" aria-label={copy.ui.lessonSequence} className="management-guide-previous-next">
      <Button
        type="button"
        variant="text"
        disabled={!previous}
        aria-label={previous ? `${copy.ui.previousLesson}: ${previous.title}` : copy.ui.firstLessonAria}
        onClick={() => previous && onSelect(previous.key)}
      >
        <span>
          <small>{copy.ui.previousLesson}</small>
          <strong>{previous ? previous.title : copy.ui.firstLesson}</strong>
        </span>
      </Button>
      <Button
        type="button"
        variant="text"
        disabled={!nextAction}
        aria-label={next
          ? `${copy.ui.nextLesson}: ${next.title}`
          : (isReturnAction ? copy.ui.returnToStartAria : copy.ui.moreGuidanceAria)}
        onClick={() => nextAction && onSelect(nextAction.key)}
      >
        <span>
          <small>{isReturnAction ? copy.ui.guideComplete : copy.ui.nextLesson}</small>
          <strong>{next
            ? next.title
            : (isReturnAction ? copy.ui.returnToStart : copy.ui.moreGuidance)}
          </strong>
        </span>
      </Button>
    </Box>
  );
}

function LessonFrame({
  copy,
  lesson,
  label,
  lead,
  intro,
  activeStage,
  activeStages,
  evidence,
  previous,
  next,
  returnLesson,
  onSelect,
  readerRef,
  children,
}) {
  const titleId = `management-guide-lesson-title-${lesson.key}`;

  return (
    <Paper
      component="article"
      id="management-guide-reader"
      ref={readerRef}
      tabIndex={-1}
      aria-labelledby={titleId}
      className="management-guide-lesson"
      elevation={0}
      data-testid="active-lesson"
    >
      {intro && (
        <Box className="management-guide-lesson-intro">
          <Typography className="management-guide-intro-eyebrow">{intro.eyebrow}</Typography>
          <Typography>{intro.statement}</Typography>
        </Box>
      )}
      <header className="management-guide-lesson-header">
        <Typography className="management-guide-lesson-label">{label}</Typography>
        <Typography id={titleId} component="h2" variant="h3">{lesson.title}</Typography>
        <Typography className="management-guide-lesson-lead">{lead}</Typography>
      </header>
      <ReasoningRail copy={copy} activeStage={activeStage} activeStages={activeStages} />
      <EvidenceReminder copy={copy} />
      <Box className="management-guide-lesson-body">{children}</Box>
      <Box className="management-guide-lesson-actions">
        <KalopsiaDialog copy={copy} lessonKey={lesson.key} lessonTitle={lesson.title} evidence={evidence} />
      </Box>
      <PreviousNext
        copy={copy}
        previous={previous}
        next={next}
        returnLesson={returnLesson}
        onSelect={onSelect}
      />
    </Paper>
  );
}

function StartLesson({ copy, startLesson, foundationLessons, ...props }) {
  return (
    <LessonFrame
      {...props}
      copy={copy}
      lesson={startLesson}
      label={copy.ui.startHere}
      lead={copy.hero.supporting}
      intro={{ eyebrow: copy.hero.eyebrow, statement: copy.hero.statement }}
      evidence={copy.lessonOne.evidence}
    >
      <Box component="section" className="management-guide-ask-block">
        <Typography component="h3">{copy.ui.ask}</Typography>
        <Typography>{copy.reasoning.prompt}</Typography>
      </Box>
      <Box component="details" className="management-guide-stage-details">
        <Typography component="summary">{copy.ui.stageQuestions}</Typography>
        <Box component="dl">
          {copy.reasoning.stages.map((stage) => (
            <Box key={stage.name}>
              <Typography component="dt">{stage.name}</Typography>
              <Typography component="dd">{stage.question}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="section" aria-labelledby="foundation-questions-heading">
        <Typography id="foundation-questions-heading" component="h3">{copy.ui.beginWithQuestions}</Typography>
        <List component="ol" disablePadding data-testid="foundation-questions" className="management-guide-foundation-list">
          {foundationLessons.map((lesson) => (
            <ListItemButton
              component="li"
              key={lesson.key}
              onClick={() => props.onSelect(lesson.key)}
            >
              <span className="management-guide-foundation-number">{lesson.position}</span>
              <ListItemText primary={lesson.title} secondary={lesson.summary} />
            </ListItemButton>
          ))}
        </List>
      </Box>
      <Box component="details" className="management-guide-future-topics">
        <Typography component="summary">{copy.ui.futureTopics}</Typography>
        <Box data-testid="business-domains" className="management-guide-domain-list">
          {copy.futureDomains.map((domain) => (
            <Box key={domain.name}>
              <Typography component="h4">{domain.name}</Typography>
              <Typography>{domain.description}</Typography>
              <Typography className="management-guide-coming-soon">{copy.ui.comingLater}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </LessonFrame>
  );
}

function LessonOne({ copy, foundationLessons, ...props }) {
  const lesson = copy.lessonOne;
  return (
    <LessonFrame
      {...props}
      copy={copy}
      lesson={foundationLessons[0]}
      label={lesson.label}
      lead={lesson.lead}
      activeStage={foundationLessons[0].reasoningStage}
      evidence={lesson.evidence}
    >
      <Box component="section" className="management-guide-ask-block">
        <Typography component="h3">{copy.ui.ask}</Typography>
        <Box component="ul" className="management-guide-list">
          {lesson.points.map((point) => <li key={point}>{point}</li>)}
        </Box>
      </Box>
      <Box component="section" className="management-guide-example-block">
        <Typography className="management-guide-block-label">{copy.ui.simpleExample}</Typography>
        <Typography component="h3">{lesson.exampleTitle}</Typography>
        <Box className="management-guide-perspective-list">
          {lesson.perspectives.map((perspective) => (
            <Box key={perspective.name}>
              <Typography component="h4">{perspective.name}</Typography>
              <Typography>{perspective.question}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Typography className="management-guide-principle">{lesson.caution}</Typography>
    </LessonFrame>
  );
}

function LessonTwo({ copy, foundationLessons, ...props }) {
  const lesson = copy.lessonTwo;
  return (
    <LessonFrame
      {...props}
      copy={copy}
      lesson={foundationLessons[1]}
      label={lesson.label}
      lead={lesson.lead}
      activeStage={foundationLessons[1].reasoningStage}
      evidence={lesson.evidence}
    >
      <Box component="section" className="management-guide-ask-block">
        <Typography component="h3">{copy.ui.comparisonPrompt}</Typography>
        <Box component="ul" className="management-guide-comparison-list">
          {lesson.comparisons.map((comparison) => <li key={comparison}>{comparison}</li>)}
        </Box>
      </Box>
      <Box component="section" className="management-guide-example-block">
        <Typography className="management-guide-block-label">{copy.ui.simpleExample}</Typography>
        <Typography component="h3">{lesson.example.observation}</Typography>
        <Typography>{lesson.example.prompt}</Typography>
        <Box component="ul" className="management-guide-list">
          {lesson.example.checks.map((check) => <li key={check}>{check}</li>)}
        </Box>
      </Box>
      <Box className="management-guide-principles">
        {lesson.principles.map((principle) => <Typography key={principle}>{principle}</Typography>)}
      </Box>
    </LessonFrame>
  );
}

function LessonThree({ copy, foundationLessons, ...props }) {
  const lesson = copy.lessonThree;
  return (
    <LessonFrame
      {...props}
      copy={copy}
      lesson={foundationLessons[2]}
      label={lesson.label}
      lead={lesson.lead}
      activeStage={foundationLessons[2].reasoningStage}
      evidence={lesson.evidence}
    >
      <Box component="section" className="management-guide-example-block">
        <Typography className="management-guide-block-label">{copy.ui.simpleExample}</Typography>
        <Box className="management-guide-equation" aria-label={lesson.equation.ariaLabel}>
          <span>{lesson.equation.salesValue}</span>
          <span aria-hidden="true">=</span>
          <span>{lesson.equation.orderCount}</span>
          <span aria-hidden="true">×</span>
          <span>{lesson.equation.averageOrderValue}</span>
        </Box>
      </Box>
      <Box component="section" className="management-guide-ask-block">
        <Typography component="h3">{copy.ui.ask}</Typography>
        <Box component="ul" className="management-guide-list">
          {lesson.questions.map((question) => <li key={question}>{question}</li>)}
        </Box>
      </Box>
      <Typography className="management-guide-principle">{lesson.principle}</Typography>
      <Box component="details" className="management-guide-decomposition-details">
        <Typography component="summary">{copy.ui.deeperPrompt}</Typography>
        <Box
          className="management-guide-decomposition-path"
          aria-label={lesson.path.join(` ${copy.ui.pathSeparatorAria} `)}
        >
          {lesson.path.map((item, index) => (
            <React.Fragment key={item}>
              <span>{item}</span>
              {index < lesson.path.length - 1 && <span aria-hidden="true">→</span>}
            </React.Fragment>
          ))}
        </Box>
      </Box>
      <Box className="management-guide-next-step">
        <Typography component="h3">{copy.ui.naturalNextStep}</Typography>
        <Typography>{lesson.nextStep}</Typography>
      </Box>
    </LessonFrame>
  );
}

function LessonConcept({ model }) {
  if (!model) return null;

  if (model.type === 'definitions') {
    return (
      <Box component="dl" className="management-guide-concept-definitions">
        {model.items.map((item) => (
          <Box key={item.term}>
            <Typography component="dt">{item.term}</Typography>
            <Typography component="dd">{item.description}</Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box className="management-guide-concept-sequence" aria-label={model.ariaLabel}>
      {model.items.map((item, index) => (
        <React.Fragment key={item}>
          <span className="management-guide-concept-item">{item}</span>
          {index < model.separators.length && (
            <span aria-hidden="true" className="management-guide-concept-separator">
              {model.separators[index]}
            </span>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

function DomainLesson({ copy, lesson, ...props }) {
  return (
    <LessonFrame
      {...props}
      copy={copy}
      lesson={lesson}
      label={lesson.label}
      lead={lesson.lead}
      activeStages={lesson.reasoningStages}
      evidence={lesson.evidence}
    >
      <Box component="section" className="management-guide-ask-block">
        <Typography component="h3">{copy.ui.ask}</Typography>
        <Box
          component={lesson.orderedQuestions ? 'ol' : 'ul'}
          className={lesson.orderedQuestions
            ? 'management-guide-list management-guide-numbered-list'
            : 'management-guide-list'}
        >
          {lesson.questions.map((question) => <li key={question}>{question}</li>)}
        </Box>
      </Box>

      <LessonConcept model={lesson.model} />

      <Box component="section" className="management-guide-example-block">
        <Typography className="management-guide-block-label">{copy.ui.simpleExample}</Typography>
        <Typography component="h3">{lesson.example.heading}</Typography>
        {lesson.example.facts.length > 0 && (
          <Box component="ul" className="management-guide-list">
            {lesson.example.facts.map((fact) => <li key={fact}>{fact}</li>)}
          </Box>
        )}
        {lesson.example.limits?.length > 0 && (
          <Box className="management-guide-example-limits">
            <Typography component="h4">{lesson.example.limitLabel}</Typography>
            <Box component="ul" className="management-guide-list">
              {lesson.example.limits.map((limit) => <li key={limit}>{limit}</li>)}
            </Box>
          </Box>
        )}
        <Box className="management-guide-example-conclusion">
          <Typography component="h4">{lesson.example.conclusionLabel}</Typography>
          <Typography>{lesson.example.conclusion}</Typography>
        </Box>
        <Typography className="management-guide-example-qualification">
          {lesson.example.qualification}
        </Typography>
      </Box>

      {lesson.distinctions?.length > 0 && (
        <Box className="management-guide-principles">
          {lesson.distinctions.map((distinction) => (
            <Typography key={distinction}>{distinction}</Typography>
          ))}
        </Box>
      )}

      <Typography className="management-guide-principle">{lesson.principle}</Typography>
      {lesson.warning && (
        <Typography className="management-guide-secondary-warning">{lesson.warning}</Typography>
      )}

      <Box className="management-guide-next-step">
        <Typography component="h3">{copy.ui.naturalNextStep}</Typography>
        <Typography>{lesson.nextStep}</Typography>
      </Box>
    </LessonFrame>
  );
}

function ActiveLesson({ lesson, copy, startLesson, foundationLessons, ...props }) {
  const lessonProps = {
    ...props,
    copy,
    startLesson,
    foundationLessons,
  };
  if (lesson.key === 'performance') return <LessonOne {...lessonProps} />;
  if (lesson.key === 'comparison') return <LessonTwo {...lessonProps} />;
  if (lesson.key === 'decomposition') return <LessonThree {...lessonProps} />;
  if (lesson.domainKey) return <DomainLesson {...lessonProps} lesson={lesson} />;
  return <StartLesson {...lessonProps} />;
}

export default function ManagementGuide() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [language, setLanguage] = useState(getInitialLanguage);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const readerRef = useRef(null);
  const desktopNavigationScrollRef = useRef(null);
  const previousLessonKey = useRef(null);
  const copy = MANAGEMENT_GUIDE_CONTENT_BY_LANGUAGE[language];
  const {
    startLesson,
    foundationLessons,
    domainGroups,
    implementedLessons,
    implementedLessonsByKey,
  } = createLessonModel(copy);
  const requestedLessonKey = searchParams.get('lesson') || startLesson.key;
  const activeLesson = implementedLessonsByKey[requestedLessonKey] || startLesson;
  const activeIndex = implementedLessons.findIndex((lesson) => lesson.key === activeLesson.key);
  const previous = activeIndex > 0 ? implementedLessons[activeIndex - 1] : null;
  const next = activeIndex < implementedLessons.length - 1 ? implementedLessons[activeIndex + 1] : null;

  useEffect(() => {
    if (previousLessonKey.current && previousLessonKey.current !== activeLesson.key) {
      readerRef.current?.focus();
      if (typeof readerRef.current?.scrollIntoView === 'function') {
        const reduceMotion = typeof window.matchMedia === 'function'
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        readerRef.current.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'start',
        });
      }
    }
    previousLessonKey.current = activeLesson.key;
  }, [activeLesson.key]);

  const selectLesson = (lessonKey) => {
    if (!implementedLessonsByKey[lessonKey]) return;
    setMobileNavigationOpen(false);
    if (lessonKey === activeLesson.key) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    if (lessonKey === startLesson.key) nextSearchParams.delete('lesson');
    else nextSearchParams.set('lesson', lessonKey);
    setSearchParams(nextSearchParams);
  };

  const selectLanguage = (nextLanguage) => {
    if (!MANAGEMENT_GUIDE_CONTENT_BY_LANGUAGE[nextLanguage] || nextLanguage === language) return;
    setLanguage(nextLanguage);
    try {
      window.localStorage.setItem(MANAGEMENT_GUIDE_LANGUAGE_STORAGE_KEY, nextLanguage);
    } catch (error) {
      // The Guide remains usable when browser storage is unavailable.
    }
  };

  return (
    <Box className="management-guide-root" lang={language}>
      <a className="management-guide-skip-link" href="#management-guide-reader">{copy.ui.skipToLesson}</a>
      <Box component="header" className="management-guide-header" data-testid="management-guide-header">
        <Container maxWidth={false} className="management-guide-header-inner">
          <Typography component="h1" className="management-guide-header-title">
            {copy.hero.title}
          </Typography>
          <GuideLanguageSelector
            language={language}
            onChange={selectLanguage}
            label={copy.ui.languageSelectorLabel}
          />
        </Container>
      </Box>
      <Box component="main" id="management-guide-main" tabIndex={-1}>
        <Container maxWidth={false} className="management-guide-container">
          <Box className="management-guide-reader-layout">
            <Paper component="aside" variant="outlined" className="management-guide-desktop-navigation">
              <Box
                ref={desktopNavigationScrollRef}
                className="management-guide-desktop-navigation-scroll"
              >
                <LessonNavigation
                  copy={copy}
                  startLesson={startLesson}
                  foundationLessons={foundationLessons}
                  domainGroups={domainGroups}
                  activeKey={activeLesson.key}
                  onSelect={selectLesson}
                  testId="desktop-lesson-navigation"
                  revealActive
                  scrollContainerRef={desktopNavigationScrollRef}
                />
              </Box>
            </Paper>
            <Box className="management-guide-reader-column">
              <MobileLessonSelector
                copy={copy}
                startLesson={startLesson}
                foundationLessons={foundationLessons}
                domainGroups={domainGroups}
                numberedLessonCount={implementedLessons.length - 1}
                activeLesson={activeLesson}
                activeKey={activeLesson.key}
                open={mobileNavigationOpen}
                onOpen={() => setMobileNavigationOpen(true)}
                onClose={() => setMobileNavigationOpen(false)}
                onSelect={selectLesson}
              />
              <ActiveLesson
                lesson={activeLesson}
                copy={copy}
                startLesson={startLesson}
                foundationLessons={foundationLessons}
                previous={previous}
                next={next}
                returnLesson={startLesson}
                onSelect={selectLesson}
                readerRef={readerRef}
              />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
